import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import CTABanner from '../components/CTABanner';
import { Tractor, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const roles = [
  'Farm Labourers & Field Workers',
  'Harvesters & Crop Pickers',
  'Greenhouse Workers',
  'Food Processing Staff',
  'Nursery & Landscaping Workers',
  'Irrigation & Equipment Support',
  'Packaging & Sorting Workers',
];

const benefits = [
  'Seasonal and year-round workers available',
  'Experience with BC agricultural operations',
  'Flexible scheduling for harvest seasons',
  'Full WCB coverage and compliance',
  'Scalable crews for peak demand',
  'Pre-screened and reliable workers',
];

const AgriculturePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Agriculture Staffing BC - WorkNation Labour Ltd | Farm Workers British Columbia</title>
        <meta name="description" content="WorkNation Labour Ltd provides agricultural staffing across BC. Farm labourers, harvesters, greenhouse workers, food processing staff. Seasonal and year-round. Contact us for fast placement." />
        <link rel="canonical" href="https://worknation.ca/industries/agriculture" />
      </Helmet>
      <Header />
      <main>
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-green-50 via-white to-emerald-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-4">
              <Tractor className="text-green-600 mr-3" size={36} aria-hidden="true" />
              <span className="text-green-600 font-semibold text-lg uppercase tracking-wide">Industry Spotlight</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-6">Agriculture Staffing in British Columbia</h1>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mb-8">
              WorkNation Labour Ltd supplies reliable agricultural workers to farms, greenhouses, nurseries, and food processing operations across British Columbia. From the fertile Fraser Valley to growing operations throughout the province, we provide the seasonal and year-round workforce your agricultural business needs.
            </p>
            <Button onClick={() => navigate('/employers')} className="bg-[#003366] hover:bg-[#004488] text-white px-8 py-6 text-lg" data-testid="agriculture-cta">
              Get Agriculture Workers Now <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Agriculture Roles We Fill</h2>
              <ul className="space-y-3">
                {roles.map((role, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} aria-hidden="true" />
                    {role}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Why Employers Choose Us</h2>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start text-gray-700">
                    <CheckCircle className="text-[#003366] mr-3 flex-shrink-0 mt-0.5" size={20} aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6">Agricultural Staffing Across BC</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              British Columbia&apos;s agricultural sector is a cornerstone of the province&apos;s economy, from berry farms and orchards in the Fraser Valley to greenhouse operations and food processing facilities. The seasonal nature of agriculture creates intense demand for reliable workers during planting, growing, and harvest seasons.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              WorkNation Labour Ltd understands these cycles and maintains a ready pool of experienced agricultural workers who can be deployed quickly when you need them. Whether you need a small crew for a family farm or dozens of workers for a large-scale operation, we scale to meet your needs.
            </p>
            <p className="text-gray-700 leading-relaxed">
              All workers are covered by WCB, and we handle all payroll and administrative requirements so you can focus on what matters\u2014growing your business.
            </p>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6 text-center">Request Agriculture Workers</h2>
            <ContactForm type="employer" />
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default AgriculturePage;
